import { FC, ReactNode } from 'react';
import { Period } from './Period';
import { cn } from '../../lib/utils';
import type { Entry } from '../../types/cv';

interface EntryBlockProps {
  entry: Entry;
  /** Bold heading line — company, institution, project name. */
  title?: ReactNode;
  /** Line under it — position, degree, role. */
  role?: ReactNode;
  /** Appended to the role line, dimmed. */
  location?: string;
  /** One-line context under the role. */
  note?: string;
  children?: ReactNode;
  /** True when nested subentries (e.g. engagements) are rendered inside — relaxes the print break rule so the block can split between them. */
  grouped?: boolean;
  /** Fold role/location onto the title line instead of giving them their own line below — for entries plain enough not to need one (awards, certificates, publications). */
  oneLine?: boolean;
}

/**
 * The repeating unit: "Title ............ period", a role line, an optional
 * note, then bullets. One of these never splits across a page — unless
 * `grouped`, where the subentries inside are the atomic unit instead
 * (see print.css).
 */
export const EntryBlock: FC<EntryBlockProps> = ({
  entry,
  title,
  role,
  location,
  note,
  children,
  grouped,
  oneLine,
}) => {
  const meta = (role || location) && (
    <>
      {role}
      {location && <span className="cv-loc"> · {location}</span>}
    </>
  );

  return (
    <article className={cn('cv-entry', grouped && 'cv-entry--grouped')}>
      <div className="cv-entry-head">
        {title && (
          <h3 className="cv-org">
            {entry.url ? <a href={entry.url}>{title}</a> : title}
            {oneLine && meta && <span className="cv-org-meta"> · {meta}</span>}
          </h3>
        )}
        <Period entry={entry} />
      </div>

      {!oneLine && meta && <p className="cv-role">{meta}</p>}

      {note && <p className="cv-note">{note}</p>}

      {entry.keywords && entry.keywords.length > 0 && (
        <div className="cv-chips">
          {entry.keywords.map(keyword => (
            <span className="cv-chip" key={keyword}>
              {keyword}
            </span>
          ))}
        </div>
      )}

      {entry.highlights && entry.highlights.length > 0 && (
        <ul className="cv-bullets">
          {entry.highlights.map((highlight, i) => (
            <li key={i}>{highlight}</li>
          ))}
        </ul>
      )}

      {children}
    </article>
  );
};

export default EntryBlock;
